import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  EConsentRequest,
  EConsentResponse,
  ENVIRONMENT,
  OriginationTcpa,
  TcpaConsentResponse,
} from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';
import { LoggingService } from '@ld.consumer-portal/utils';

@Injectable({
  providedIn: 'root',
})
export class EConsentService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  private get cpApiUrl(): string {
    return this.env.appSettings.cpApiUrl || '';
  }

  private get apiNamespace(): string {
    return this.env.appSettings.apiNamespace || 'api/v1';
  }

  async getEConsent(loanId: string, borrowerId: string): Promise<EConsentResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/borrowers/${borrowerId}/econsent`;

    try {
      return await firstValueFrom(this.http.get<EConsentResponse>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error fetching e-consent for loan ${loanId}, borrower ${borrowerId}`);
      throw new Error(`Failed to fetch e-consent: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async updateEConsent(loanId: string, borrowerId: string, request: EConsentRequest): Promise<EConsentResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/borrowers/${borrowerId}/econsent`;

    try {
      return await firstValueFrom(this.http.put<EConsentResponse>(url, request));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error updating e-consent for loan ${loanId}, borrower ${borrowerId}`);
      throw new Error(`Failed to update e-consent: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getTcpaConsent(loanId: string, borrowerId: string): Promise<TcpaConsentResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/borrowers/${borrowerId}/tcpaconsent`;

    try {
      return await firstValueFrom(this.http.get<TcpaConsentResponse>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error fetching TCPA consent for loan ${loanId}, borrower ${borrowerId}`);
      throw new Error(`Failed to fetch TCPA consent: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async updateTcpaConsent(loanId: string, borrowerId: string, request?: OriginationTcpa): Promise<TcpaConsentResponse> {
    let url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/borrowers/${borrowerId}/tcpaconsent`;

    if (request?.clientToken) {
      url = `${url}/${request.clientToken}`;
    }

    try {
      return await firstValueFrom(this.http.post<TcpaConsentResponse>(url, {}));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error updating TCPA consent for loan ${loanId}, borrower ${borrowerId}`);
      throw new Error(`Failed to update TCPA consent: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async updateInstantVerificationConsent(
    loanId: string,
    borrowerId: string,
    request: EConsentRequest
  ): Promise<EConsentResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/borrowers/${borrowerId}/instantverificationconsent`;

    try {
      return await firstValueFrom(this.http.put<EConsentResponse>(url, request));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error updating instant verification consent for loan ${loanId}, borrower ${borrowerId}`);
      throw new Error(`Failed to update instant verification consent: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
}
